import {
  IObservableArray,
  computed,
  makeAutoObservable,
  observable,
} from "mobx";
import { TaskWithContent } from "../DTO/ServerResponseType/Task/Task";
import TaskServerClient from "../ServerClients/TaskServerClient";
import Fuse from "fuse.js";
import { LittleTaskApi } from "../ProfileWindow/AdminWindows/TaskListWindow/DTO/LittleTaskAPI";
import { TaskSortMap } from "../utils/sort/taskListSort/options";
import { SortOptionsForTaskList } from "../Enums/TaskStoreOptions";

const fuseOptions = {
  isCaseSensitive: false,
  shouldSort: false,
  keys: ["task Name", "taskPrivateName"],
};

export enum TaskListViews {
  cards,
  table,
}

export class TasksStore {
  tasks: IObservableArray<TaskWithContent> = observable.array([]);
  showDeleteModal: boolean = false;
  deleteTaskData!: LittleTaskApi;
  searchTaskNamePattern: string = "";
  fuse: Fuse<TaskWithContent> = new Fuse(this.tasks, fuseOptions);
  taskListView: TaskListViews = TaskListViews.cards;
  chosenTaskStatuseIds: IObservableArray<number> = observable.array([]);
  chosenTaskTypesIds: IObservableArray<number> = observable.array([]);
  chosenTaskExecutorsIds: IObservableArray<number> = observable.array([]);
  chosenTaskTagsIds: IObservableArray<string> = observable.array([]);
  chosenSortedOption: SortOptionsForTaskList =
    SortOptionsForTaskList.modificationTimeDesc;
  constructor() {
    makeAutoObservable(this);
  }

  onDeleteModalOpen = (taskData: LittleTaskApi) => {
    this.showDeleteModal = true;
    this.deleteTaskData = taskData;
  };

  onDeleteModalClose = () => {
    this.showDeleteModal = false;
  };

  setTasks = (tasks: TaskWithContent[]) => {
    this.tasks.replace(tasks);
    this.fuse.setCollection(tasks);
  };

  deleteTask = (taskId: string) => {
    TaskServerClient.sendToDeleteTaskAsync(taskId).then(() => {
      this.tasks.replace(
        this.tasks.filter((a: TaskWithContent) => a.taskId !== taskId)
      );
    });
  };

  onChangeTaskView = () => {
    this.taskListView =
      this.taskListView === TaskListViews.cards
        ? TaskListViews.table
        : TaskListViews.cards;
  };

  @computed get taskViewButtonText(): string {
    let taskViewButtonText = "";

    if (this.taskListView === TaskListViews.table)
      taskViewButtonText = "Карточки";

    if (this.taskListView === TaskListViews.cards)
      taskViewButtonText = "Таблица";

    return taskViewButtonText;
  }
  onChangeSearchTaskNamePattern = (
    event: React.FormEvent<HTMLInputElement>
  ) => {
    this.searchTaskNamePattern = event.currentTarget.value;
  };

  private onChangeFilter<T>(
    currentTargetChecked: boolean,
    currentTargetValue: T,
    changedArray: IObservableArray<T>
  ) {
    if (currentTargetChecked) changedArray.push(currentTargetValue);
    else
      changedArray.replace(
        changedArray.filter((el) => el !== currentTargetValue)
      );
  }

  onChangeStatusFilter = (event: React.FormEvent<HTMLInputElement>) => {
    this.onChangeFilter(
      event.currentTarget.checked,
      parseInt(event.currentTarget.value),
      this.chosenTaskStatuseIds
    );
  };

  onChangeTypeFilter = (event: React.FormEvent<HTMLInputElement>) => {
    this.onChangeFilter(
      event.currentTarget.checked,
      parseInt(event.currentTarget.value),
      this.chosenTaskTypesIds
    );
  };

  onChangeExecutorFilter = (event: React.FormEvent<HTMLInputElement>) => {
    this.onChangeFilter(
      event.currentTarget.checked,
      parseInt(event.currentTarget.value),
      this.chosenTaskExecutorsIds
    );
  };

  onChangeTagFilter = (event: React.FormEvent<HTMLInputElement>) => {
    this.onChangeFilter(
      event.currentTarget.checked,
      event.currentTarget.value,
      this.chosenTaskTagsIds
    );
  };

  onChangeSortedOptions = (event: React.FormEvent<HTMLSelectElement>) => {
    let eventOption =
      event.currentTarget.options[event.currentTarget.selectedIndex];

    if (eventOption) this.chosenSortedOption = parseInt(eventOption.value);
  };

  @computed get searchedAndFilteredAndSortedTasks(): TaskWithContent[] {
    let searchedAndFilteredAndSortedTasks: TaskWithContent[] = [];

    // search
    if (this.searchTaskNamePattern !== "") {
      let fuseResult = this.fuse.search(this.searchTaskNamePattern);
      searchedAndFilteredAndSortedTasks = fuseResult.map(
        ({ item }: { item: TaskWithContent }) => item
      );
    } else searchedAndFilteredAndSortedTasks = this.tasks.toJSON();

    // filter
    if (this.chosenTaskStatuseIds.length !== 0)
      searchedAndFilteredAndSortedTasks =
        searchedAndFilteredAndSortedTasks.filter((task: TaskWithContent) =>
          this.chosenTaskStatuseIds.includes(task.taskStatusId)
        );

    if (this.chosenTaskTypesIds.length !== 0)
      searchedAndFilteredAndSortedTasks =
        searchedAndFilteredAndSortedTasks.filter((task: TaskWithContent) =>
          this.chosenTaskTypesIds.includes(task.taskContent.type)
        );

    if (this.chosenTaskExecutorsIds.length !== 0)
      searchedAndFilteredAndSortedTasks =
        searchedAndFilteredAndSortedTasks.filter((task: TaskWithContent) =>
          this.chosenTaskExecutorsIds.includes(task.taskContent.executor)
        );

    if (this.chosenTaskTagsIds.length !== 0)
      searchedAndFilteredAndSortedTasks =
        searchedAndFilteredAndSortedTasks.filter((task: TaskWithContent) => {
          let result = true;

          this.chosenTaskTagsIds.forEach((taskTagId) => {
            result =
              result &&
              task.taskTags.map(({ tag }) => tag.tagId).includes(taskTagId);
          });
          return result;
        });

    // sort
    let sortOptions = TaskSortMap.get(this.chosenSortedOption);
    if (sortOptions !== undefined)
      searchedAndFilteredAndSortedTasks =
        searchedAndFilteredAndSortedTasks.sort(sortOptions.func);

    return searchedAndFilteredAndSortedTasks;
  }

  loadAllTasksAsync = async (): Promise<TaskWithContent[] | never> => {
    return TaskServerClient.sendToGetAllTasksAsync(true).then((tasks) => {
      this.setTasks(tasks);
      return tasks;
    });
  };
}
